/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  CHART TOKENS — instructor performance
 * ─────────────────────────────────────────────────────────────────────────────
 * Colour is assigned by the JOB it does, not by taste, and the categorical set
 * below was run through the palette validator rather than eyeballed:
 *
 *   node scripts/validate_palette.js "#d45180,#2a78d6,#eb6834,#1baf7a,#4a3aa7" \
 *        --mode light --surface "#ffffff"
 *   → lightness band PASS · chroma floor PASS · CVD sep PASS (worst adjacent
 *     ΔE 9.2 deutan) · normal-vision PASS (worst 26.9) · ALL CHECKS PASS
 *
 * Slot 1 is the brand's deep pink (`colors.secondaryDark`), so the screen still
 * reads as this product while clearing every gate. For reference, the palette
 * the existing StudioAnalytics screen uses FAILS three of the checks
 * (`#f9a825` outside the lightness band, `#78909c` below the chroma floor, and
 * two colours under 3:1) — it is left alone here rather than changed underneath
 * a screen this work does not own.
 *
 * ⚠️ The one WARN this set carries: aqua `#1baf7a` sits at 2.82:1 on white. The
 * relief rule therefore applies wherever it appears — every chart using the
 * categorical set below ships VISIBLE DIRECT LABELS, not colour alone.
 */

/** Categorical — identity. Assigned in FIXED ORDER, never cycled, never by rank. */
export const SERIES = [
  '#d45180', // 1 · brand deep pink
  '#2a78d6', // 2 · blue
  '#eb6834', // 3 · orange
  '#1baf7a', // 4 · aqua  (2.82:1 — direct labels required)
  '#4a3aa7', // 5 · violet
] as const;

/**
 * The 6th+ category folds into this, rather than a generated hue.
 * Grey is deliberate: "Other" is not an identity, it is the absence of one.
 */
export const OTHER = '#9aa0a6';

/** The instructor's own number, wherever there is exactly one series. */
export const PRIMARY = SERIES[0];

/**
 * Capacity / benchmark / "what was on offer". Recessive on purpose — it is the
 * backdrop the real number is read against, never a series competing with it.
 */
export const GHOST = '#e6e6e9';
export const GHOST_INK = '#8a8a93';

/**
 * Sequential — magnitude. ONE hue, light → dark. Used by the calendar heatmap.
 * Step 100 → 600 of the reference blue ramp.
 */
export const SEQ = [
  '#cde2fb',
  '#9ec5f4',
  '#6da7ec',
  '#3987e5',
  '#256abf',
  '#184f95',
] as const;

/** Recessive chrome. Solid hairlines — never dashed, which reads as a threshold. */
export const GRID = '#ececed';
export const AXIS_INK = '#8a8a93';

/** Status. Reserved — never reused as "series 6". */
export const STATUS = {
  good: '#0f8a4d',
  warn: '#b26a00',
  bad: '#c0362c',
} as const;

/** Recharts axis/grid defaults, so every chart on the page agrees. */
export const axisProps = {
  stroke: AXIS_INK,
  tickLine: false,
  axisLine: false,
  style: { fontSize: 11 },
} as const;

export const gridProps = {
  stroke: GRID,
  strokeDasharray: '0',
  vertical: false,
} as const;

export const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * The calendar's heat bands live with the calendar, not here.
 *
 * A previous `heatStep()` hardcoded 30/50/70/90 and was removed: real studio
 * fill sits between 5% and 40%, so every day landed on the same step and the
 * ramp carried no information. The bands are now even 20-point steps declared
 * in ScheduleCalendar, next to the legend that prints them.
 */
